import * as THREE from "three";
import { EmptyGroup, getRoofModuleMeshes, GetRoundNum } from "./Common";
import { GetModuleMap, norModuleCol, selModuleCol, SetShadeMeshOnModule } from "./ModuleControl";
import { ft2m, inch2m } from "../../../../../Constants";

import { useViewerStore } from "../../../../../store/store";
import { getPathnameIncludes } from "../../../../../helpers/getPathname";
import { LoadingContext } from "../../../../../store/loadingMessageStore";
import sleep from "../../../../../helpers/sleep";
import { MODULE_GRAPH, SHADE_CALCULATIONS, siteCalculationState } from "../../../../../services/siteCalculationState";
import { isAiRunMode, shouldRunModuleGraphCalc } from "./TreeImpactEligibility";
import workerScript from './shade.worker.ts?worker';
import { mapSize, pixelCount, pixelSize } from "./TerrainControl";
import { Viewer } from "../Viewer-Factory/Viewer";
import { MeshInfoData, RoofFacePtHashData } from "../../../../../@types/types";
import { MeshType } from "../../../../../@types/enums";
import { testMesh } from "./Loader";
import { getOneDrawDebuggerOptions, getShadeWorkerRenderMeshTypes, getShadeWorkerSunVectorDebug } from './DebugDraw';

const shadeUnit = 0.889;
const shadeGeo = new THREE.SphereGeometry(0.05, 32, 32),
    shadeMat = new THREE.MeshStandardMaterial({ color: 0x0000ff });
let webWorker: any;
export const shadeMesh = new THREE.Mesh(shadeGeo, shadeMat);
const BG_SHADE_YIELD_EVERY = 8;

export function CheckInsidePos(polygon: any[], pos: any, axis = 'z') {
    const { x } = pos, zVal = pos[axis];
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const zi = polygon[i][axis];
        const xj = polygon[j].x;
        const zj = polygon[j][axis];
        const intersect = ((zi > zVal) !== (zj > zVal)) && (x < (xj - xi) * (zVal - zi) / (zj - zi) + xi)
        if (intersect) inside = !inside
    }
    return inside;
}

function GetInPos(lastPos2D: { x: number, z: number }, pos2DArr: { x: number, z: number }[], x: number, z: number, maxX: number, minX: number, maxZ: number, minZ: number) {
    const inPos: any = { x: x - lastPos2D.x, z: z - lastPos2D.z };
    inPos.xn = Math.round(inPos.x / shadeUnit);
    inPos.zn = Math.round(inPos.z / shadeUnit);
    inPos.flag = (z !== minZ && z !== maxZ && x !== minX && x !== maxX) && CheckInsidePos(pos2DArr, { x, z });
    return inPos;
}

function GetInPos2DArr(pos2DArr: { x: number, z: number }[], posSize: { maxX: number, minX: number, maxZ: number, minZ: number }, rotX: number) {
    const inPos2DArr = [], { maxX, minX, maxZ, minZ } = posSize, lastPos2D = pos2DArr[pos2DArr.length - 1];
    
    for (let z = minZ; z <= maxZ; z += shadeUnit) {
        for (let x = maxX; x >= minX; x -= shadeUnit) {
            inPos2DArr.push(GetInPos(lastPos2D, pos2DArr, x, z, maxX, minX, maxZ, minZ));
        }
    }
    
    return inPos2DArr;
}

export function GetSizePos(pos2DArr: { x: number, z: number }[]) {
    const maxX = Math.max(...pos2DArr.map(o => o.x)),
        minX = Math.min(...pos2DArr.map(o => o.x)),
        maxZ = Math.max(...pos2DArr.map(o => o.z)),
        minZ = Math.min(...pos2DArr.map(o => o.z));
    return { maxX, minX, maxZ, minZ };
}

export function AddShadeMesh(shapeMesh: any, pos2DArr: { x: number, z: number }[], holeArr: any[], rotX: number) {

    const posSize = GetSizePos(pos2DArr);
    const inPos2DArr = GetInPos2DArr(pos2DArr, posSize, rotX);

    // Get shade points inside the region
    const filtered = inPos2DArr.filter(item => item.flag);
    const count = filtered.length;

    // Prepare the instanced mesh
    const instancedShade = new THREE.InstancedMesh(
        shadeGeo, // geometry
        shadeMat.clone(), // material
        count
    );

    instancedShade.name = "instancedShadeMesh";

    instancedShade.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // for updates

    // Optional: tag the mesh for selection
    (instancedShade as any).shadePoint = true;

    let shadeArrForRoof: any[] = []

    // Set up transformations for each instance
    const shadePointInstanceObject3D = new THREE.Object3D();

    filtered.forEach((inPos, i) => {
        shadePointInstanceObject3D.position.set(inPos.x, inPos.z, 0);
        shadePointInstanceObject3D.updateMatrix();
        instancedShade.setMatrixAt(i, shadePointInstanceObject3D.matrix);
        shadeArrForRoof.push({
            shadePointInstanceObject3D: shadePointInstanceObject3D.clone(),
            position: { x: inPos.x, y: inPos.z, z: 0 },
            monthArr: new Array(13).fill(1), // Default value
            ptHashVal: 100, // Default value
            onModule: false, // Default value
        })

    });
    instancedShade.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3);

    //make shade mesh blue colored
    const shadeColor = new THREE.Color(0x0000ff);
    for (let i = 0; i < count; i++) {
        instancedShade.instanceColor.setXYZ(i, shadeColor.r, shadeColor.g, shadeColor.b);
    }
    instancedShade.instanceColor.needsUpdate = true;

    instancedShade.instanceMatrix.needsUpdate = true;
    instancedShade.name = "shadePoint";
    instancedShade.visible = false
    shapeMesh.add(instancedShade);
    shapeMesh.userData.shadeArrForRoof = shadeArrForRoof;

    // Meta-data
    shapeMesh.pos2DArr = pos2DArr;
    shapeMesh.holeArr = holeArr;
    shapeMesh.inPos2DArr = inPos2DArr;
    shapeMesh.inPosSize = { minX: posSize.minX, minZ: posSize.minZ, maxX: posSize.maxX, maxZ: posSize.maxZ, w: posSize.maxX - posSize.minX, h: posSize.maxZ - posSize.minZ };
}



export function AddShadePosArr(face: any, shapeMesh: any) {
    const shadePosArr: any[] = [], inPosArr: any[] = [];

    const shadePointArr = shapeMesh.userData.shadeArrForRoof

    shadePointArr.forEach((item: any) => {
        shapeMesh.add(item.shadePointInstanceObject3D);
        item.shadePointInstanceObject3D.updateMatrixWorld();
        item.shadePointInstanceObject3D.updateWorldMatrix(true, false);
        item.worldPos = item.shadePointInstanceObject3D.getWorldPosition(new THREE.Vector3())
        item.visible = false;
        const { x, y, z } = item.position;
        shadePosArr.push(item.worldPos)
        inPosArr.push({ x, y: z, z: y });
    });
    face.shadePosArr = shadePosArr;
    face.pos2DArr = shapeMesh.pos2DArr;
    face.holeArr = shapeMesh.holeArr;
    face.inPos2DArr = inPosArr;

}

export function isShadeGatingActive() {
    return useViewerStore.getState().isLargeDesignShadeGatingActive;
}

export function setShadeGatingState(active: boolean, roofFaceIds: string[] = []) {
    useViewerStore.setState({
        isLargeDesignShadeGatingActive: active,
        shadeEnabledRoofFaceIds: active ? Array.from(new Set(roofFaceIds)) : [],
    });
}

export function addShadeEnabledRoofIds(roofFaceIds: string[] = []) {
    if (!roofFaceIds.length) return;
    const { shadeEnabledRoofFaceIds } = useViewerStore.getState();
    const merged = Array.from(new Set([...(shadeEnabledRoofFaceIds || []), ...roofFaceIds]));
    useViewerStore.setState({
        isLargeDesignShadeGatingActive: true,
        shadeEnabledRoofFaceIds: merged,
    });
}

export function isShadeEnabledForRoof(roofFaceId?: string) {
    if (!roofFaceId) return true;
    const { isLargeDesignShadeGatingActive, shadeEnabledRoofFaceIds } = useViewerStore.getState();
    if (!isLargeDesignShadeGatingActive) return true;
    return (shadeEnabledRoofFaceIds || []).includes(roofFaceId);
}

export function ensureRoofShadeGeometry(face: any, roofMesh: any) {
    if (!face || !roofMesh) return false;
    const shadeArr = roofMesh.userData?.shadeArrForRoof;
    const hasShadeGeometry = Array.isArray(shadeArr) && shadeArr.length > 0 && Array.isArray(face.shadePosArr) && face.shadePosArr.length > 0;
    if (hasShadeGeometry) return false;
    if (!roofMesh.pos2DArr) return false;
    AddShadeMesh(roofMesh, roofMesh.pos2DArr, roofMesh.holeArr || [], roofMesh.rotX || 0);
    AddShadePosArr(face, roofMesh);
    return true;
}

function syncShadeGatingForGlobalRecalc(self: Viewer, faces: any[]) {
    const allFaces = self.roofs?.[0]?.faces || [];
    // Auto-adjust only for global recalculation flows (save/finalize/report).
    if (!allFaces.length || faces.length !== allFaces.length) return;

    const roofMeshes = self.roofMeshArr || [];
    const moduleRoofFaceIds = roofMeshes
        .filter((roofMesh: any) => !roofMesh?.disableModule && getRoofModuleMeshes(roofMesh).length > 0)
        .map((roofMesh: any) => String(roofMesh.roofFaceId));

    const totalRoofs = roofMeshes.length;
    const shouldGateToModuleRoofs =
        totalRoofs > 10
        && moduleRoofFaceIds.length > 0
        && moduleRoofFaceIds.length < totalRoofs;

    setShadeGatingState(shouldGateToModuleRoofs, shouldGateToModuleRoofs ? moduleRoofFaceIds : []);
}

function SetRayPosInfo(modelGroup: any) {
    modelGroup.rayPosInfo = true;
    modelGroup.traverse((child: any) => {
        if (!child.worldPos || !child.isMesh) return;
        if (child.meshType === 'obst') {
            const { verPosArr, height } = child;
            var minZ = Infinity, maxZ = -Infinity, maxY = -Infinity, minX = Infinity, maxX = -Infinity;
            verPosArr.forEach((verPos: { x: number; y: number; z: number; }) => {
                const { x, y, z } = verPos;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (z < minZ) minZ = z;
                if (z > maxZ) maxZ = z;
                if (y + height / 2 > maxY) maxY = y + height / 2;
            });
            child.rayPosInfo = { minZ, maxZ, maxY, minX, maxX };
        } else if (child.meshType === 'tree') {
            if (!child.incrossArr) child.incrossArr = [];
            const { scale, parent } = child, { x, y, z } = parent.position, treeWidth = scale.x;
            child.rayPosInfo = { maxZ: z + treeWidth, maxY: y + scale.y, minX: x - treeWidth, maxX: x + treeWidth };
        } else if (child.meshType === 'roof' || child.roofFaceId) {
            const { min, max } = new THREE.Box3(new THREE.Vector3()).setFromObject(child);
            child.rayPosInfo = { maxZ: max.z, maxY: max.y, minX: min.x, maxX: max.x };
        }
    })
}

export async function GenShadeOnFace(faces: any[], self: Viewer) {
    const { roofMeshArr, sunVecArr, modelGroup, customTerrainData, buildingAlt, terrainView, viewMode, moduleMeshArr } = self;
    const terrainFlag = terrainView && viewMode === '3d';
    const loadingContext = new LoadingContext();
    const isBgCalc = useViewerStore.getState().isBgCalc;
    //check if viewMode is 2d , then switch to 3d
    if (self.viewMode === '2d') {
        loadingContext.setMessage({ title: 'Switching to 3D View...', message: 'Please wait' });
        self.setCameraView('3d');
        await sleep(1000)
    }
    loadingContext.setToBackground(isBgCalc);
    if (isBgCalc) {
        // Yield once so camera/orbit stays responsive before heavy serialization.
        await sleep(0);
    }

    const irradianceMonth = self.irradianceMonth || useViewerStore.getState().irradianceMonth;// for removing redundant irradiancemonth
    const weights = irradianceMonth;
    if (!(modelGroup as any).rayPosInfo) SetRayPosInfo(modelGroup);

    syncShadeGatingForGlobalRecalc(self, faces);
    const filteredFaces = faces.filter((face) => isShadeEnabledForRoof(face?.roofFaceId));
    if (!filteredFaces.length) {
        useViewerStore.setState({ refreshingShade: false, shadeCalculatingLength: 0, shadeCalculatingIdx: 0 });
        loadingContext.clearAll();
        return;
    }

    if (!sunVecArr || !Array.isArray(sunVecArr) || !weights || !Array.isArray(weights)) {
        useViewerStore.setState({ refreshingShade: false, shadeCalculatingLength: 0, shadeCalculatingIdx: 0 });
        loadingContext.clearAll();
        return;
    }

    // Build a broad relevance bound so we don't serialize obviously irrelevant meshes
    // for small incremental runs (helps background interactivity).
    let relevanceMinY = Infinity;
    let relevanceMinZ = Infinity;
    filteredFaces.forEach((face) => {
        const faceMesh = roofMeshArr.find(item => item.roofFaceId === face.roofFaceId);
        if (!faceMesh?.posArr) return;
        faceMesh.posArr.forEach((pos: { y: number; z: number }) => {
            if (pos.y < relevanceMinY) relevanceMinY = pos.y;
            if (pos.z < relevanceMinZ) relevanceMinZ = pos.z;
        });
    });

    const candidateMeshes: any[] = [];
    modelGroup.traverse((child: any) => {
        if (!child.worldPos || !child.isMesh) return;
        const { meshType, rayPosInfo } = child;
        if (meshType !== 'racking-module' && rayPosInfo && Number.isFinite(relevanceMinY) && Number.isFinite(relevanceMinZ)) {
            const { maxY = Infinity, maxZ = Infinity } = rayPosInfo;
            if (maxY < relevanceMinY || maxZ < relevanceMinZ) return;
        }
        candidateMeshes.push(child);
    });

    let meshInfoArr: any[] = [];
    for (let idx = 0; idx < candidateMeshes.length; idx++) {
        const child: any = candidateMeshes[idx];
        const { meshType, worldPos, scale, rotX, rotY, geometry, rayPosInfo, name } = child;
        const geomJSON = meshType === 'roof' ? geometry.toJSON() : geometry.toNonIndexed().toJSON();

        if (meshType === 'roof') {
            const attributeNames = Object.keys(geometry.attributes);
            const attributes: { [key: string]: any } = {};
            const groups = geometry.groups;

            attributeNames.forEach(attributeName => {
                const attributeJSON = {
                    array: Array.from(geometry.attributes[attributeName].array),
                    itemSize: geometry.attributes[attributeName].itemSize,
                    type: "Float32Array"
                };
                attributes[attributeName] = attributeJSON;
            });

            geomJSON.data = { attributes, groups };
        }

        let meshInfo: MeshInfoData = { meshType, geomJSON, worldPos, scale, rayPosInfo };

        const roofHeightInfo = {
            roofHeight: (useViewerStore.getState().wallEditedHeight - useViewerStore.getState().wallInitialHeight) || 0
        }

        if (meshType === 'obst') {
            const { height, obstId, verFloor, sclY, delStatus, selfRot, isPolygonWithFourNodes, shapeNum, length, isDefaultObstruction } = child;
            meshInfo = { ...meshInfo, height, rotX, rotY, obstId, verFloor, sclY, id: obstId, selfRot, isPolygonWithFourNodes, shapeNum, isDefaultObstruction, delStatus: delStatus ?? false, length };
        } else if (meshType === 'roof') {
            if (child.name?.includes('wallMesh')) {
                meshInfo.pos2DArr = child.userData.positions
                meshInfo.meshType = 'wall';
                meshInfo.name = child.name;
                meshInfo.id = child.name;
                meshInfo.initialHeight = child.userData.initialHeight;
            } else {
                meshInfo.roofFaceId = child.roofFaceId;
                meshInfo.id = child.userData.roof_tag_id;
            }
            meshInfo = { ...meshInfo, rotX, rotY };
        } else if (meshType === 'tree') {
            const { delStatus, userData } = child.parent, { treeId, height, crown_radius } = userData;
            meshInfo = { ...meshInfo, treeId, height, crown_radius, id: treeId, delStatus: delStatus ?? false };
        }
        meshInfo.roofHeightInfo = roofHeightInfo;
        meshInfoArr.push(meshInfo);

        if (isBgCalc && idx > 0 && idx % BG_SHADE_YIELD_EVERY === 0) {
            await sleep(0);
        }
    }

    for (let idx = 0; idx < moduleMeshArr.length; idx++) {
        const mesh = moduleMeshArr[idx];
        //Add only if ballasted racking
        if(!mesh.userData.isBallastedRackType) continue;
        const { meshType, geometry } = mesh;

        //Module World Position
        const worldPos = new THREE.Vector3();
        mesh.getWorldPosition(worldPos);

        const geomJSON = geometry.toNonIndexed().toJSON();

        //Module World Rotation Quaternion
        const worldQuat = new THREE.Quaternion();
        const worldScale = new THREE.Vector3();

        mesh.updateMatrixWorld(true);
        mesh.matrixWorld.decompose(worldPos, worldQuat, worldScale);

        const { min, max } = new THREE.Box3(new THREE.Vector3()).setFromObject(mesh);
        const rayPosInfo = { maxZ: max.z, maxY: max.y, minX: min.x, maxX: max.x };
        
        const meshInfo = {
            meshType: 'racking-module',
            geomJSON,
            worldPos: worldPos,
            worldQuat: {
                x: worldQuat.x,
                y: worldQuat.y,
                z: worldQuat.z,
                w: worldQuat.w,
            },
            scale: {
                x: worldScale.x,
                y: worldScale.y,
                z: worldScale.z,
            },
            id: mesh.id,
            roofFaceId: mesh.roofId,
            rayPosInfo,
        };


        meshInfoArr.push(meshInfo);
        if (isBgCalc && idx > 0 && idx % BG_SHADE_YIELD_EVERY === 0) {
            await sleep(0);
        }
    }


    loadingContext.setMessage({ title: 'Calculating Shade...', message: `Calculating shade: Roof 1 of ${filteredFaces.length}` });
    useViewerStore.setState({ shadeCalculatingLength: filteredFaces.length, shadeCalculatingIdx: 0, refreshingShade: true });

    let faceInfoArr = [];
    for (const [calcIdx, face] of filteredFaces.entries()) {
        const { roofFaceId, roofId, shadePosArr } = face;
        const faceMesh = roofMeshArr.find(item => item.roofFaceId === roofFaceId);

        var fMinX = Infinity, fMaxX = -Infinity, fMinY = Infinity, fMinZ = Infinity, fMaxZ = -Infinity;
        if (faceMesh && faceMesh.posArr) {
            faceMesh.posArr?.forEach((pos: { x: number; y: number; z: number; }) => {
                const { x, y, z } = pos;
                if (x < fMinX) fMinX = x; if (x > fMaxX) fMaxX = x;
                if (y < fMinY) fMinY = y;
                if (z < fMinZ) fMinZ = z; if (z > fMaxZ) fMaxZ = z;
            });
        }

        const faceInfo = { roofId, roofIdx: calcIdx, roofFaceId, shadePosArr, fMinY, fMinZ };
        faceInfoArr.push(faceInfo);
    }

    const shadeWorkerOptions = getOneDrawDebuggerOptions().shadeWorker;
    const renderMeshTypes = getShadeWorkerRenderMeshTypes(shadeWorkerOptions);
    const sunVectorDebug = getShadeWorkerSunVectorDebug(shadeWorkerOptions);
    const params = { faceInfoArr, meshInfoArr, sunVecArr, weights, customTerrainData, buildingAlt, terrainFlag, mapSize, pixelCount, renderMeshTypes, sunVectorDebug };

    const clearRefreshingShadeState = () => {
        useViewerStore.setState({ refreshingShade: false, shadeCalculatingLength: 0, shadeCalculatingIdx: 0 });
    };

    await new Promise<void>((resolve, reject) => {
        if (webWorker) {
            webWorker.terminate();
            webWorker = null;
        }
        webWorker = new (workerScript as any)();

        webWorker.onerror = () => {
            loadingContext.clearAll();
            clearRefreshingShadeState();
            webWorker?.terminate();
            webWorker = null;
            reject(new Error('Shade calculation worker failed'));
        };

        webWorker.onmessage = async (event: MessageEvent<any>) => {
        const { action } = event.data;

        if (action === 'loader') {
            // Update loader text from web worker thread
            const { roofIdx } = event.data;
            useViewerStore.setState({ shadeCalculatingIdx: roofIdx });
            loadingContext?.setMessage({ title: 'Calculating Shade...', message: `Calculating shade: Roof ${roofIdx + 1} of ${filteredFaces.length}` });
        }
        else if (action === 'complete') {
            try {
            // Update values on completing shade calculations in worker thread
            const { faceResultArr, rayIntersectArr } = event.data;
            const newRoofFaceIds = Array.from(new Set(faceResultArr.map((result: any) => result.roofFaceId)));
            const oldRayIntersectArr = useViewerStore.getState().rayIntersectArr || [];
            const mergedRayIntersectArr = oldRayIntersectArr
                .filter((ray: any) => !newRoofFaceIds.includes(ray.roofFaceId))
                .concat(rayIntersectArr);
            useViewerStore.setState({ rayIntersectArr: mergedRayIntersectArr, refreshingShade: false, shadeCalculatingLength: 0, shadeCalculatingIdx: 0 });
            // console.log(`Background Task (Shade) Completed in ${Math.round(event.data.time)}ms:`); // faceResultArr
            faceResultArr.forEach((result: any) => {
                const { roofFaceId, avrAccess, ptHashAverage, shadePtArr } = result;
                const face = self.roofs[0].faces.find((item: any) => item.roofFaceId === roofFaceId);
                if (!face) return;
                const faceMesh = roofMeshArr.find(item => item.roofFaceId === roofFaceId);
                if (!faceMesh) return;

                const shadeMeshArr = faceMesh.userData.shadeArrForRoof
                const ptHashArr = shadePtArr.map((ptData: { access: number[]; ptHashVal: number; }, ptIdx: number) => {
                    const { access, ptHashVal } = ptData;
                    shadeMeshArr[ptIdx].ptHashVal = ptHashVal;
                    shadeMeshArr[ptIdx].monthArr = access;
                    return ptHashVal;
                });
                face.ptHashArr = ptHashArr;
                face.ptHashAverage = faceMesh.ptHashAverage = ptHashAverage;
                face.ptHashMonth = avrAccess;
            });

            modelGroup.traverse((child: any) => {
                if (child.roofFaceId) {
                    const face = self.roofs[0].faces.find((face: { roofFaceId: any; }) => face.roofFaceId === child.roofFaceId);
                    if (!face) return;
                    child.ptHashArr = face.ptHashArr
                }
            });

            loadingContext?.setMessage({ title: 'Calculating Tree Impact...', message: `Calculating Tree Multipliers` });
            RefreshTreeImpactArr(self);
            SetShadeDisplayEffect(modelGroup);
            (modelGroup as any).rayPosInfo = false;
            loadingContext.clearAll();
            resolve();
            } catch (error) {
                clearRefreshingShadeState();
                reject(error);
            } finally {
                webWorker?.terminate();
                webWorker = null;
            }
        } else {
            // For testing : To render meshes from worker thread 
            if (action === 'clear-sun-vector') {
                EmptyGroup(self.sunVectorGroup);
            } else if (action === 'sun-vector') {
                const { flagCross, start, target, append } = event.data;
                const startVec = new THREE.Vector3(start.x, start.y, start.z);
                const targetVec = new THREE.Vector3(target.x, target.y, target.z);
                const material = new THREE.LineBasicMaterial({ color: flagCross ? 0xff0000 : 0x0000ff });
                const geometry = new THREE.BufferGeometry().setFromPoints([targetVec, startVec]);
                const line = new THREE.Line(geometry, material);
                if (!append) {
                    EmptyGroup(self.sunVectorGroup);
                }
                self.sunVectorGroup.add(line);
            } else if (action === 'console') {
                const { text, content } = event.data;
                console.log(`${text}:`, content);
            } else if (action === 'render-meshes') {
                const { meshInfoArr, buildingAlt, terrainFlag, renderMeshTypes } = event.data, deltaBuildingH = buildingAlt.low + 0.2;
                const shouldRender = (meshType: string) => !renderMeshTypes || renderMeshTypes[meshType];
                // const colors = { 'roof': 0x00FF00, 'wall': 0xFFFF00, 'tree': 0x00FFFF, 'obst': 0xFF00FF, 'map': 0x0000FF };
                const colors: { [key: string]: number } = {
                    "roof": 0x00FF00,
                    "wall": 0xFFFF00,
                    "tree": 0x00FFFF,
                    "obst": 0xFF00FF,
                    "map": 0x0000FF,
                    "module": 0x0000FF,
                };

                function getBufferMesh(meshType: string, geomJSON: any, transparent?: boolean) {
                    const geometriesLoader = new THREE.BufferGeometryLoader();
                    const geometry = geometriesLoader.parse(geomJSON);
                    const matProperties = transparent ? { color: colors[meshType], wireframe: true, transparent: true } : { color: colors[meshType], side: THREE.DoubleSide, wireframe: false };
                    const material = new THREE.MeshBasicMaterial(matProperties);
                    //Add polygon offset to avoid z-fighting
                    material.polygonOffset = true;
                    material.polygonOffsetFactor = -3;
                    material.polygonOffsetUnits = 1;
                    material.needsUpdate = true;
                    const bufferMesh = new THREE.Mesh(geometry, material);
                    bufferMesh.castShadow = true;
                    return bufferMesh;
                }

                function GetWallGroup(meshInfo: any) {
                    const { meshType, worldPos, rotX, rotY, pos2DArr } = meshInfo;
                    // Create the wall shape
                    const wallShape = new THREE.Shape();
                    const firstVer = pos2DArr[0];
                    wallShape.moveTo(firstVer.x, firstVer.z);

                    for (let i = 1; i < pos2DArr.length - 1; i++) {
                        const ver = pos2DArr[i];
                        wallShape.lineTo(ver.x - (meshInfo.roofHeightInfo.roofHeight || 0), ver.z);
                    }
                    wallShape.lineTo(pos2DArr[pos2DArr.length - 1].x, pos2DArr[pos2DArr.length - 1].z);
                    const extrudeSettings = { depth: 0.0001, bevelEnabled: false };
                    const wallGeo = new THREE.ExtrudeGeometry(wallShape, extrudeSettings);
                    const wallMat = new THREE.MeshStandardMaterial({ color: 0xFFFF00 });
                    //Add polygon offset to avoid z-fighting
                    wallMat.polygonOffset = true;
                    wallMat.polygonOffsetFactor = -4;
                    wallMat.polygonOffsetUnits = 1;
                    wallMat.needsUpdate = true;
                    const wallMesh: any = new THREE.Mesh(wallGeo, wallMat);
                    wallMesh.rotation.x = rotX;
                    wallMesh.meshType = meshType;
                    wallMesh.userData = meshInfo;
                    const wallInGroup = new THREE.Group(), wallOutGroup = new THREE.Group();
                    wallInGroup.add(wallMesh);
                    wallInGroup.rotation.z = -rotX;
                    wallOutGroup.add(wallInGroup);
                    wallOutGroup.rotation.y = rotY;
                    wallOutGroup.position.set(worldPos.x, 0, worldPos.z);
                    return wallOutGroup;
                }

                function GetRoofGroup(meshInfo: any) {
                    let { meshType, geomJSON, worldPos, rotX, rotY, initialHeight } = meshInfo;
                    if (!initialHeight) initialHeight = 0;
                    const shapeMesh: any = getBufferMesh(meshType, geomJSON);
                    shapeMesh.rotation.x = Math.PI / 2;
                    shapeMesh.meshType = meshType;
                    shapeMesh.userData = meshInfo;
                    const parTest = new THREE.Group(), subTest = new THREE.Mesh();
                    parTest.name ="azimuthGroup"
                    parTest.add(subTest);
                    subTest.add(shapeMesh);
                    subTest.name = "tiltMesh"
                    subTest.rotation.x = rotX;
                    parTest.rotation.y = rotY;
                    parTest.position.set(worldPos.x, worldPos.y + meshInfo.roofHeightInfo.roofHeight, worldPos.z);
                    return parTest;
                }

                function GetTreeGroup(meshInfo: { meshType: string, geomJSON: any, worldPos: { x: number, z: number }, height: number, crown_radius: number }) {
                    const { meshType, geomJSON, worldPos, height, crown_radius } = meshInfo;
                    const treeGroup = new THREE.Group()
                    const wrapperMesh: any = getBufferMesh(meshType, geomJSON, true);
                    treeGroup.add(wrapperMesh);
                    treeGroup.position.set(worldPos.x, 0, worldPos.z);
                    wrapperMesh.scale.set(crown_radius / 2 * 1, height * 1, crown_radius / 2 * 1);
                    wrapperMesh.position.y = height / 2;
                    wrapperMesh.meshType = meshType;
                    wrapperMesh.userData = meshInfo;
                    treeGroup.position.y = terrainFlag ? GetAlt(customTerrainData, treeGroup.position, deltaBuildingH) : 0;
                    return treeGroup;
                }

                function getObstGroup(meshInfo: any) {

                    const { meshType, geomJSON, worldPos, height, rotX, rotY, verFloor, scale, selfRot, isPolygonWithFourNodes, shapeNum, length, isDefaultObstruction } = meshInfo;
                    const obstMesh = getBufferMesh(meshType, geomJSON);
                    const outGroup = new THREE.Group(), inGroup = new THREE.Group();
                    outGroup.add(inGroup);
                    inGroup.add(obstMesh);
                    const depth = verFloor ? Math.tan(-rotX) * length / 2 : 0;
                    inGroup.rotation.set(verFloor ? 0 : rotX || 0, 0, 0);
                    outGroup.rotation.set(0, rotY, 0);
                    inGroup.rotation.x = verFloor ? 0 : rotX || 0;
                    obstMesh.scale.y = height + depth;
                    if (isPolygonWithFourNodes && isDefaultObstruction) {
                        obstMesh.rotation.y = selfRot;
                    }
                    if (shapeNum !== 4) {
                        obstMesh.position.y = height / 2 - depth
                    }
                    obstMesh.scale.set(scale.x, scale.y, scale.z);
                    outGroup.position.set(worldPos.x, worldPos.y + meshInfo.roofHeightInfo.roofHeight, worldPos.z);
                    (obstMesh as any).meshType = meshType;
                    obstMesh.userData = meshInfo;
                    return outGroup;
                }

                function GetMapPlane() {
                    const planeGeo = new THREE.PlaneGeometry(mapSize, mapSize);
                    const planeMat = new THREE.MeshStandardMaterial({
                        color: 0x555555,
                        side: THREE.DoubleSide
                    });
                    const planeMesh = new THREE.Mesh(planeGeo, planeMat);
                    planeMesh.rotation.x = -Math.PI / 2;
                    return planeMesh;
                }

                function GetAlt(terrainArr: any[], pos: { x: number; y: number; z: number }, delta = 0) {
                    const { x, y, z } = pos,
                        xn = Math.round((x + mapSize / 2) / pixelSize),
                        zn = Math.round((z + mapSize / 2) / pixelSize);
                    const terrainNode = terrainArr.find((node: { x: number; z: number; }) => node && node.x === xn && node.z === zn);
                    return terrainNode ? terrainNode.py - delta : 0;
                }

                const modelGroup = new THREE.Group();
                if (shouldRender('map')) {
                    modelGroup.add(GetMapPlane());
                }

                EmptyGroup(self.trialGroup);

                meshInfoArr.forEach((meshInfo: { meshType: string, worldPos: { x: number, y: number, z: number }, geomJSON: any, height: number, crown_radius: number }) => {
                    const { meshType, worldPos } = meshInfo;
                    if (!shouldRender(meshType)) return;
                    if (meshType === 'wall') {
                        const wallOutGroup = GetWallGroup(meshInfo);
                        modelGroup.add(wallOutGroup);
                    } else if (meshType === 'roof') {
                        const roofGroup = GetRoofGroup(meshInfo);
                        modelGroup.add(roofGroup);
                    } else if (meshType === 'tree') {
                        const treeGroup = GetTreeGroup(meshInfo);
                        modelGroup.add(treeGroup);
                    } else if (meshType === 'obst') {
                        const obstGroup = getObstGroup(meshInfo);
                        modelGroup.add(obstGroup);
                    }
                    else if( meshType === 'racking-module') {
                        const { geomJSON, worldPos, worldQuat, scale } = meshInfo as any;
                        const moduleMesh = getBufferMesh(meshType, geomJSON);

                        moduleMesh.position.set(worldPos.x, worldPos.y, worldPos.z);
                        moduleMesh.quaternion.set(
                            worldQuat.x,
                            worldQuat.y,
                            worldQuat.z,
                            worldQuat.w
                        );

                        if (scale) {
                            moduleMesh.scale.set(scale.x, scale.y, scale.z);
                        }

                        (moduleMesh as any).meshType = "module";
                        moduleMesh.userData = meshInfo;

                        modelGroup.add(moduleMesh);


                    }
                });


                self.trialGroup.add(modelGroup);
            } else if (action === 'clear-meshes') {
                EmptyGroup(self.trialGroup);
            }
        }
    };

        webWorker.postMessage({ action: 'shade', params });
    });
}

export async function ensureRoofShadeReadyForModulePlacement(self: Viewer, roofFaceId: string) {
    if (!roofFaceId) return true;
    const face = self.roofs?.[0]?.faces?.find((item: any) => item.roofFaceId === roofFaceId);
    const roofMesh = self.roofMeshArr.find((item) => item.roofFaceId === roofFaceId);
    if (!face || !roofMesh) return true;

    const hasShadeGeometry = Array.isArray(roofMesh.userData?.shadeArrForRoof) && roofMesh.userData.shadeArrForRoof.length > 0
        && Array.isArray(face.shadePosArr) && face.shadePosArr.length > 0;
    if (hasShadeGeometry && isShadeEnabledForRoof(roofFaceId)) return true;

    useViewerStore.setState({ isBgCalc: true });
    addShadeEnabledRoofIds([roofFaceId]);
    ensureRoofShadeGeometry(face, roofMesh);
    await GenShadeOnFace([face], self);
    return true;
}

export function syncRayDelStatusForTrees(treeIds: string[], delStatus: boolean) {
    const { rayIntersectArr } = useViewerStore.getState();
    if (!rayIntersectArr?.length || !treeIds?.length) return;

    rayIntersectArr.forEach(ray => {
        treeIds.forEach(treeId => {
            const object = ray.objects.find(obj => obj.id === treeId);
            if (object) object.delStatus = delStatus;
        });
    });

    useViewerStore.setState({ rayIntersectArr });
}

/** Keep shade ray ghost flags aligned with tree mesh delStatus before impact calculations. */
export function syncAllTreeRayDelStatusFromMeshes(self: Viewer) {
    if (!self.treeGroup?.children?.length) return;
    self.treeGroup.children.forEach((tree: any) => {
        const treeId = tree.userData?.treeId;
        if (treeId) syncRayDelStatusForTrees([treeId], !!tree.delStatus);
    });
}

export async function ReCalcShade(self: Viewer, obstIds: string[], delStatus: boolean) {
    const { rayIntersectArr } = useViewerStore.getState();
    const { roofMeshArr, modelGroup } = self;
    const faces = self.roofs[0].faces;

    faces.forEach((face: RoofFacePtHashData) => {
        if (!isShadeEnabledForRoof(face.roofFaceId)) return;
        const { roofFaceId, shadePosArr } = face, shadeCount = shadePosArr?.length || 0;
        const faceMesh = roofMeshArr.find(item => item.roofFaceId === roofFaceId);
        if (!faceMesh) return;
        const shadeMeshArr = faceMesh.userData.shadeArrForRoof

        const faceAccess: number[] = [], ptHashArr: number[] = [];
        for (let i = 0; i < 13; i++) {
            faceAccess[i] = 0;
        }

        shadeMeshArr.forEach((shadeMesh, ptIdx) => {
            const ptRayArr = rayIntersectArr.filter(ray => ray.roofFaceId === roofFaceId && ray.ptIdx === ptIdx);
            const access = new Array(13).fill(1);

            ptRayArr.forEach(ray => {
                const { monthIdx, objects, weights } = ray;
                obstIds.forEach(obstId => {
                    const object = objects.find(obj => obj.id === obstId);
                    if (object) object.delStatus = delStatus;
                });

                const crossObjects = objects.filter(obj => !obj.delStatus);
                if (crossObjects.length > 0) {
                    access[0] -= weights[0]; // annualWeight
                    access[monthIdx + 1] -= weights[1]; // monthlyWeight
                }
            });

            access.forEach((month, idx) => {
                if (idx !== 0) faceAccess[idx] += month;
            });
            const ptHashVal = access[0] * 100;
            ptHashArr[ptIdx] = ptHashVal;
            shadeMesh.ptHashVal = ptHashVal;
            shadeMesh.monthArr = access;
        });

        face.ptHashArr = ptHashArr;

        const totalPhHashVal = ptHashArr.reduce((total, item) => total + item, 0);
        const ptHashAverage = ptHashArr.length ? totalPhHashVal / ptHashArr.length : 0;
        face.ptHashAverage = faceMesh.ptHashAverage = ptHashAverage;
        const avrAccess: number[] = [];
        faceAccess.forEach((item, idx) => {
            if (idx === 0) return;
            const avrAccessVal = shadeCount ? item / shadeCount : 0;
            avrAccess.push(avrAccessVal);
        });
        face.ptHashMonth = avrAccess;

    });

    RefreshTreeImpactArr(self);

    modelGroup.traverse((child: any) => {
        if (child.roofFaceId) {
            const face = self.roofs[0].faces.find((face: RoofFacePtHashData) => face.roofFaceId === child.roofFaceId);
            if (!face) return;
            child.ptHashArr = face.ptHashArr
        }
    });

    useViewerStore.setState({ rayIntersectArr });
    SetShadeDisplayEffect(modelGroup);
    (modelGroup as any).rayPosInfo = false;

    if (!isAiRunMode()) {
        siteCalculationState.needsUpdate(MODULE_GRAPH);
        await siteCalculationState.useCalculations(MODULE_GRAPH);
    }
}

export function RefreshTreeImpactArr(self: Viewer) {
    const { rayIntersectArr } = useViewerStore.getState();
    const { treeGroup, roofMeshArr, roofs } = self;
    const faces = roofs[0].faces;
    const treeArr = treeGroup.children.map(t => t.userData.treeId);

    faces.forEach((face: any) => {
        if (!isShadeEnabledForRoof(face.roofFaceId)) return;
        const faceMesh = roofMeshArr.find(item => item.roofFaceId === face.roofFaceId);
        if (!faceMesh) return;

        const shadeMeshArr = faceMesh.userData.shadeArrForRoof;
        shadeMeshArr.forEach((shadeMesh, ptIdx) => {
            shadeMesh.treeArr = [];
            const ptRayArr = rayIntersectArr.filter(ray => ray.roofFaceId === face.roofFaceId && ray.ptIdx === ptIdx);

            treeArr.forEach(treeId => {
                let access = 1;

                ptRayArr.forEach(ray => {
                    const crossObjects = ray.objects.filter(obj => obj.id !== treeId && !obj.delStatus);
                    if (crossObjects.length > 0) access -= ray.weights[0] // Annual Shade Weight;
                });

                shadeMesh.treeArr.push({ treeId, ptHashGhost: access * 100 });
            });
        });
    });
}

export function GetDifferArr(ptValArr: number[]) {
    const differArr: number[] = [];
    ptValArr.forEach(ptHashVal => {
        if (!differArr.includes(ptHashVal)) differArr.push(ptHashVal);
    });
    return differArr.sort();
}

export function SetShadeFinalResult(self: Viewer) {
    if (isAiRunMode()) {
        return;
    }
    const { roofs, roofMeshArr, modelGroup } = self;
    const faces = roofs[0].faces;

    var totalRoofArea = 0, totalModuleArea = 0;
    const totalSolarArr = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const irrMonthTotal = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const modeTotalSolarArr = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const modeIrrMonthTotal = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

    SetShadeMeshOnModule(roofMeshArr);
    const irradianceMonth = self.irradianceMonth ?? useViewerStore.getState().irradianceMonth;
    faces.forEach((face: { size: number; ptHashMonth: number[]; roofFaceId: string; }) => {
        const selRoofMesh = roofMeshArr.find(item => item.roofFaceId === face.roofFaceId);
        if (!selRoofMesh || selRoofMesh.disableModule) return;
        totalRoofArea += face.size;
        face.ptHashMonth?.forEach((monthVal, monthIdx) => {
            totalSolarArr[monthIdx] += monthVal * face.size
        });

        const modeArr = selRoofMesh.children.filter((item: any) => item.module);
        const modeSize = modeArr.reduce((total: number, mode: any) => total + ((mode.selSize.w * mode.selSize.h) / (ft2m * ft2m)), 0);
        totalModuleArea += modeSize;

        irradianceMonth.forEach((month: any, monthIdx: number) => {
            month.forEach((hour: { monthly: number; }) => {
                irrMonthTotal[monthIdx] += hour.monthly * face.size;
                modeIrrMonthTotal[monthIdx] += hour.monthly * modeSize;
            });
        });

        const shadeArr = selRoofMesh.userData.shadeArrForRoof;
        shadeArr.forEach(shade => {
            shade.monthArr?.forEach((monthVal, monthIdx) => {
                if (monthIdx > 0) modeTotalSolarArr[monthIdx - 1] += (monthVal / shadeArr.length) * modeSize;
            });
        });
    });

    const localFacePtHash = faces.map((f: { roofFaceId: string; size: number; ptHashAverage: number; oriAng: any; }) => ({
        roofFaceId: f.roofFaceId,
        size: f.size,
        ptHashAverage: f.ptHashAverage,
        ...f.oriAng,
    }));

    const modelRoot = modelGroup?.children?.[0];
    if (modelRoot?.traverse) {
        modelRoot.traverse((child: any) => {
            const face = faces.find((f: any) => f.roofFaceId === child.roofFaceId);
            if (child.roofFaceId === undefined || child.disableModule || !face?.oriAng) return;
            child.oriAng = face.oriAng;
        });
    }

    const totalSolar = totalSolarArr.reduce((total, monthVal) => total + monthVal, 0);
    const modeTotalSolar = modeTotalSolarArr.reduce((total, monthVal) => total + monthVal, 0);
    const safePercent = (value: number, denom: number, decimals = 0) => {
        if (!Number.isFinite(value) || !Number.isFinite(denom) || denom <= 0) return 0;
        const raw = (value / denom) * 100;
        return decimals > 0 ? GetRoundNum(raw, decimals) : Math.round(raw);
    };
    const avrSolarArr = totalSolarArr.map(monthVal => safePercent(monthVal, totalRoofArea));
    const avrIrradiance = irrMonthTotal.map(monthVal => safePercent(monthVal, totalRoofArea));
    const modeAvrSolarArr = modeTotalSolarArr.map(monthVal => safePercent(monthVal, totalModuleArea, 2));
    const modeAvrIrradiance = modeIrrMonthTotal.map(monthVal => safePercent(monthVal, totalModuleArea, 2));
    const modeCircleSolar = safePercent(modeTotalSolar / 12, totalModuleArea);

    // Only update store when values actually changed to avoid "Maximum update depth exceeded" (infinite re-render loop)
    const state = useViewerStore.getState() as any;
    const prevSaved = state.savedRoofWiseAccessMultiplierDataArray;
    const sameSaved = Array.isArray(prevSaved) && prevSaved.length === localFacePtHash.length
        && localFacePtHash.every((f: any, i: number) => {
            const prev = prevSaved[i];
            return prev?.roofFaceId === f.roofFaceId
                && Object.is(GetRoundNum(prev?.ptHashAverage ?? 0, 4), GetRoundNum(f.ptHashAverage ?? 0, 4));
        });
    const sameCircle = Object.is(state.modeCircleSolar, modeCircleSolar);
    const sameChartSolar = Array.isArray(state.modeChartSolar) && state.modeChartSolar.length === modeAvrSolarArr.length
        && state.modeChartSolar.every((v: number, i: number) => Object.is(v, modeAvrSolarArr[i]));
    const sameChartIrr = Array.isArray(state.modeChartIrr) && state.modeChartIrr.length === modeAvrIrradiance.length
        && state.modeChartIrr.every((v: number, i: number) => Object.is(v, modeAvrIrradiance[i]));
    if (!sameSaved || !sameCircle || !sameChartSolar || !sameChartIrr) {
        useViewerStore.setState({
            savedRoofWiseAccessMultiplierDataArray: localFacePtHash,
            modeCircleSolar,
            modeChartSolar: modeAvrSolarArr,
            modeChartIrr: modeAvrIrradiance,
        });
    }
}

export function GetShadeCol(val: number, rgb: boolean) {
    if (val >= 95) return rgb ? '#FFFF00' : 0xFFFF00;
    else if (val >= 90) return rgb ? '#FFEB00' : 0xFFEB00;
    else if (val >= 85) return rgb ? '#FFD600' : 0xFFD600;
    else if (val >= 80) return rgb ? '#FFC200' : 0xFFC200;
    else if (val >= 75) return rgb ? '#FFA300' : 0xFFA300;
    else if (val >= 70) return rgb ? '#FF8F00' : 0xFF8F00;
    else if (val >= 65) return rgb ? '#FF7000' : 0xFF7000;
    else if (val >= 60) return rgb ? '#FF5C00' : 0xFF5C00;
    else if (val >= 55) return rgb ? '#FF4700' : 0xFF4700;
    else if (val >= 50) return rgb ? '#FF3300' : 0xFF3300;
    // else if (val >= 45) return rgb ? '#FF0000' : 0xFF0000;
    else return rgb ? '#DCDCDC' : 0xdcdcdc;
}

export function SetDisplayShadePoints(self: Viewer, shadeType?: string) {
    const { roofs, modelGroup, roofMeshArr } = self;
    if (!roofs || !roofs[0] || !roofs[0].faces || !roofs[0].faces.length) return;
    (modelGroup as any).irrType = GetShadePathname(shadeType);
    if ((modelGroup as any).irrType) {
        const activeRoofMeshArr = roofMeshArr.filter(item => !item.disableModule);
        (modelGroup as any).totalShadeCount = activeRoofMeshArr.length;
        siteCalculationState.useCalculations(SHADE_CALCULATIONS);
    }

    SetShadeDisplayEffect(modelGroup);
}

export function SetShadeDisplayEffect(modelGroup: any, showShadePoint?: boolean) {
    const moduleSolarAccess = useViewerStore.getState().moduleSolarAccess;

    modelGroup.traverse((child: any) => {
        if (child.roofFaceId) {
            const shadeArr = child.userData.shadeArrForRoof;
            const { ptHashArr } = child;
            const color = child.oriColor;
            // const map = child.oriMap; // child.material.color.setHex(color);
            // child.material.map = map;
            // child.material.needsUpdate = true;

            // child.material.color.setHex(color);
            // child.material.map = map;
            // child.material.needsUpdate = true;


            const moduleArr = getRoofModuleMeshes(child);
            moduleArr.forEach((module: any) => {
                if (moduleSolarAccess) {
                    SetModuleAsColored(module, module.irrColor);
                } else {
                    SetModuleAsTextured(module);
                }
            });
        }
    })
}

export function SetModuleAsTextured(module: any) {
    const showCol = module.select ? selModuleCol : norModuleCol;
    module.material.color.setHex(showCol);
    module.material.map = GetModuleMap(module.dir);
    module.material.needsUpdate = true;
}

export function SetModuleAsColored(module: any, color: any) {
    const showCol = module.select ? selModuleCol : color;
    module.material.color.setHex(showCol);
    module.material.map = undefined;
    module.material.needsUpdate = true;
}

function GetShadePathname(shadeType?: string) {
    if (shadeType === 'module') return 'module';
    else if (getPathnameIncludes('solar-access')) return 'point';
    else if (getPathnameIncludes('irradiance')) return 'irradiance'; // || pathname.includes('module')
    else if (getPathnameIncludes('module')) return 'module';
    else if (getPathnameIncludes('what-if') && shadeType === 'whatif') return 'irradiance';
    else if (getPathnameIncludes('price-calculation')) return 'irradiance';
    else if (shadeType === 'init') return 'init';
    else return null;
}

export function RefreshShade() {
    useViewerStore.setState({ isBgCalc: true });
    // if (shadeType) SetDisplayShadePoints(self, shadeType);
    siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
    siteCalculationState.useCalculations(SHADE_CALCULATIONS);
}